import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import LogoSpinner from '../components/atoms/LogoSpinner';
import Calendar from '../components/atoms/Calendar';
import Accordion, { AccordionItem } from '../components/atoms/Accordion';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import PromotionalBanner from '../components/molecules/PromotionalBanner';
import { Product, ProductSize, ProductFlavor, NavLink, CartItemCreateData, DeliveryTier, AvailabilityDate, AvailabilitySettings, ImageOnCakeSettings, ColorPricingSettings, normalizeAvailabilitySettings } from '../types';
import { getProduct, getDeliveryTiers, getAvailabilityRange, getAvailabilitySettings, uploadFile, getFileURL, getImageOnCakeSettings, getColorPricingSettings } from '../services/firebase';
import { useCart, useCartToast } from '../contexts';
import './ProductDetailPage.css';
import { useStorefrontData } from '../hooks/useStorefrontData';
import { CAKE_COLORS, getBaseColorAdditionalPrice } from '../constants/cakeColors';
import { FULFILLMENT_WINDOWS, getFulfillmentWindowById } from '../constants/fulfillmentWindows';


const ProductDetailPage: React.FC = () => {
  const { productId, collectionName } = useParams<{ productId: string; collectionName: string }>();
  const navigate = useNavigate();
  const { operations } = useCart();
  const { showAddToCartSuccess, showCartError } = useCartToast();

  const availableColors = CAKE_COLORS;
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [selectedFlavors, setSelectedFlavors] = useState<string[]>([]);
  const [selectedShape, setSelectedShape] = useState<string>('');
  const [selectedFilling, setSelectedFilling] = useState<string>('');
  const [selectedIcing, setSelectedIcing] = useState<string>('');
  const [selectedBaseColor, setSelectedBaseColor] = useState<string>('');
  const [selectedPipingColors, setSelectedPipingColors] = useState<string[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [selectedMatchingCupcakes, setSelectedMatchingCupcakes] = useState<string>('');
  const [specialInstructions, setSpecialInstructions] = useState<string>('');
  const [hasImageOnCake, setHasImageOnCake] = useState<boolean>(false);
  const [cakeImageFile, setCakeImageFile] = useState<File | null>(null);
  const [cakeImagePreview, setCakeImagePreview] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [hasSampleImage, setHasSampleImage] = useState<boolean>(false);
  const [sampleImageFile, setSampleImageFile] = useState<File | null>(null);
  const [sampleImagePreview, setSampleImagePreview] = useState<string>('');
  const [isUploadingSampleImage, setIsUploadingSampleImage] = useState<boolean>(false);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState<boolean>(false);
  const [deliveryTiers, setDeliveryTiers] = useState<DeliveryTier[]>([]);
  const [deliveryOption, setDeliveryOption] = useState<'collection' | 'delivery'>('collection');
  const [selectedTimeWindowId, setSelectedTimeWindowId] = useState<string>('');
  const [minDeliveryPrice, setMinDeliveryPrice] = useState<number>(0);
  
  // Availability states
  const [availabilityDates, setAvailabilityDates] = useState<AvailabilityDate[]>([]);
  const [availabilitySettings, setAvailabilitySettings] = useState<AvailabilitySettings | null>(null);
  const [imageOnCakeSettings, setImageOnCakeSettings] = useState<ImageOnCakeSettings | null>(null);
  const [colorPricingSettings, setColorPricingSettings] = useState<ColorPricingSettings | null>(null);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Date | null>(null);
  const [selectedCollectionDate, setSelectedCollectionDate] = useState<Date | null>(null);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState<boolean>(false);
  const [isFlavorsDropdownOpen, setIsFlavorsDropdownOpen] = useState<boolean>(false);
  const [isShapeDropdownOpen, setIsShapeDropdownOpen] = useState<boolean>(false);
  const [isFillingDropdownOpen, setIsFillingDropdownOpen] = useState<boolean>(false);
  const [isIcingDropdownOpen, setIsIcingDropdownOpen] = useState<boolean>(false);
  const [isAddOnsDropdownOpen, setIsAddOnsDropdownOpen] = useState<boolean>(false);
  const [isMatchingCupcakesDropdownOpen, setIsMatchingCupcakesDropdownOpen] = useState<boolean>(false);
  const { getPromotionalBannerData } = useStorefrontData();
  const bannerData = getPromotionalBannerData();

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!productId) {
        setError('Product ID is missing');
        setLoading(false);
        return;
      }

      try {
        // Fetch product, delivery tiers, availability data, and image on cake settings in parallel
        const availabilityStart = new Date();
        availabilityStart.setHours(0, 0, 0, 0);
        const availabilityEnd = new Date(availabilityStart);
        availabilityEnd.setDate(availabilityEnd.getDate() + 90);

        const [productData, tiers, availabilityData, settingsData, imageOnCakeData, colorPricingData] = await Promise.all([
          getProduct(productId),
          getDeliveryTiers(),
          getAvailabilityRange(availabilityStart, availabilityEnd),
          getAvailabilitySettings(),
          getImageOnCakeSettings(),
          getColorPricingSettings().catch(() => null)
        ]);
        
        if (!productData) {
          setError('Product not found');
          setLoading(false);
          return;
        }
        
        // Debug log to see what data we're getting
        console.log('Product data:', productData);
        console.log('Fillings:', productData.fillings);
        console.log('Cover icing options:', productData.coverIcingOptions);

        setProduct(productData);
        setSelectedImage(productData.images.find(img => img.isPrimary)?.url || productData.images[0]?.url || '');
        const initialSize = productData.sizes[0] || null;
        setSelectedSize(initialSize);
        
        // If this size uses quantity tiers, default quantity to the first tier
        if (initialSize && initialSize.quantityTiers && initialSize.quantityTiers.length > 0) {
          setQuantity(initialSize.quantityTiers[0].quantity);
        }

        // Set default flavor if available
        const availableFlavors = productData.flavors.filter(f => f.available);
        if (availableFlavors.length > 0) {
          setSelectedFlavors([availableFlavors[0].name]);
        }
        
        setSelectedShape(productData.availableShapes[0] || '');
        
        // Only set these if they exist in the data
        if (productData.fillings && productData.fillings.length > 0) {
          setSelectedFilling(productData.fillings[0]);
        }
        
        if (productData.coverIcingOptions && productData.coverIcingOptions.length > 0) {
          setSelectedIcing(productData.coverIcingOptions[0]);
        }

        // Set delivery tiers and calculate minimum delivery price
        setDeliveryTiers(tiers);
        const activeTiers = tiers.filter(tier => tier.isActive);
        if (activeTiers.length > 0) {
          const minPrice = Math.min(...activeTiers.map(tier => tier.price));
          setMinDeliveryPrice(minPrice);
        }

        // Set availability and image on cake settings
        setAvailabilityDates(availabilityData);
        setAvailabilitySettings(settingsData ? normalizeAvailabilitySettings(settingsData) : null);
        setImageOnCakeSettings(imageOnCakeData);
        setColorPricingSettings(colorPricingData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load product. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [productId]);

  useEffect(() => {
    if (selectedSize) {
      // Determine effective quantity and base price per unit
      let effectiveQuantity = quantity;
      let basePricePerUnit = selectedSize.price;

      if (selectedSize.quantityTiers && selectedSize.quantityTiers.length > 0) {
        // Find the tier matching the current quantity, or fall back to the first tier
        const currentTier =
          selectedSize.quantityTiers.find(tier => tier.quantity === quantity) ||
          selectedSize.quantityTiers[0];

        effectiveQuantity = currentTier.quantity;
        basePricePerUnit = currentTier.price / currentTier.quantity;

        // Keep quantity state in sync with the selected tier quantity
        if (quantity !== effectiveQuantity) {
          setQuantity(effectiveQuantity);
        }
      }

      // Start with base price from size (per unit)
      let price = basePricePerUnit;
      
      // Add additional costs for selected flavors
      if (selectedFlavors.length > 0 && product) {
        selectedFlavors.forEach(flavorName => {
          const flavor = product.flavors.find(f => f.name === flavorName);
          if (flavor && flavor.price) {
            price += flavor.price;
          }
        });
      }
      
      // Add additional costs for selected filling
      if (selectedFilling && product && product.fillings) {
        const fillingPrice = parseFillingPrice(selectedFilling);
        if (fillingPrice > 0) {
          price += fillingPrice;
        }
      }
      
      // Add additional costs for selected cover icing
      if (selectedIcing && product && product.coverIcingOptions) {
        const icingPrice = parseIcingPrice(selectedIcing);
        if (icingPrice > 0) {
          price += icingPrice;
        }
      }
      
      // Add additional costs for selected add-ons
      if (selectedAddOns.length > 0 && product && product.addOns) {
        selectedAddOns.forEach(addOn => {
          const addOnPrice = parseAddOnPrice(addOn);
          if (addOnPrice > 0) {
            price += addOnPrice;
          }
        });
      }
      
      // Add additional costs for selected matching cupcakes
      if (selectedMatchingCupcakes && product && product.matchingCupcakes) {
        const cupcakePrice = parseMatchingCupcakePrice(selectedMatchingCupcakes);
        if (cupcakePrice > 0) {
          price += cupcakePrice;
        }
      }

      if (product?.enableBaseColor !== false) {
        price += getBaseColorAdditionalPrice(selectedBaseColor, {
          collection: product?.collection,
          productId: product?.id,
          settings: colorPricingSettings
        });
      }
      
      // Multiply by quantity
      setTotalPrice(price * effectiveQuantity);
    }
  }, [selectedSize, selectedFlavors, selectedFilling, selectedIcing, selectedAddOns, selectedMatchingCupcakes, selectedBaseColor, quantity, product, colorPricingSettings]);

  const handleFlavorToggle = (flavor: string) => {
    // If this flavor is already selected and it's not the only flavor selected, remove it
    if (selectedFlavors.includes(flavor)) {
      // Don't allow removing the last flavor - we need at least one flavor selected
      if (selectedFlavors.length > 1) {
        setSelectedFlavors(selectedFlavors.filter(f => f !== flavor));
      }
    } else {
      // If max flavors already selected, replace the first one
      if (selectedFlavors.length >= (product?.maxFlavorsSelectable || 1)) {
        // Remove first flavor and add the new one
        const updatedFlavors = [...selectedFlavors];
        updatedFlavors.shift(); // Remove first flavor
        updatedFlavors.push(flavor); // Add new flavor
        setSelectedFlavors(updatedFlavors);
      } else {
        // Otherwise just add the new flavor
        setSelectedFlavors([...selectedFlavors, flavor]);
      }
    }
  };

  const handlePipingColorToggle = (color: string) => {
    // If this color is already selected, remove it
    if (selectedPipingColors.includes(color)) {
      setSelectedPipingColors(selectedPipingColors.filter(c => c !== color));
    } else {
      // If max colors already selected (2), replace the first one
      if (selectedPipingColors.length >= 2) {
        // Remove first color and add the new one
        const updatedColors = [...selectedPipingColors];
        updatedColors.shift(); // Remove first color
        updatedColors.push(color); // Add new color
        setSelectedPipingColors(updatedColors);
      } else {
        // Otherwise just add the new color
        setSelectedPipingColors([...selectedPipingColors, color]);
      }
    }
  };

  const handleAddOnToggle = (addOn: string) => {
    if (selectedAddOns.includes(addOn)) {
      setSelectedAddOns(selectedAddOns.filter(a => a !== addOn));
    } else {
      setSelectedAddOns([...selectedAddOns, addOn]);
    }
  };

  const handleMatchingCupcakeSelect = (cupcake: string) => {
    setSelectedMatchingCupcakes(cupcake);
    setIsMatchingCupcakesDropdownOpen(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      setCakeImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCakeImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCakeImageFile(null);
    setCakeImagePreview('');
  };

  const handleSampleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      setSampleImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSampleImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveSampleImage = () => {
    setSampleImageFile(null);
    setSampleImagePreview('');
  };

  // Helper function to parse price from filling string (e.g., "Ganache +€5" -> 5)
  const parseFillingPrice = (fillingString: string): number => {
    if (fillingString.includes('+€')) {
      const priceMatch = fillingString.match(/\+€(\d+(?:\.\d+)?)/);
      return priceMatch ? parseFloat(priceMatch[1]) : 0;
    }
    return 0;
  };

  // Helper function to parse price from icing string (e.g., "Ganache +€10" -> 10)
  const parseIcingPrice = (icingString: string): number => {
    if (icingString.includes('+€')) {
      const priceMatch = icingString.match(/\+€(\d+(?:\.\d+)?)/);
      return priceMatch ? parseFloat(priceMatch[1]) : 0;
    }
    return 0;
  };

  // Helper function to parse price from add-on string (e.g., "Candles +€5" -> 5)
  const parseAddOnPrice = (addOnString: string): number => {
    if (addOnString.includes('+€')) {
      const priceMatch = addOnString.match(/\+€(\d+(?:\.\d+)?)/);
      return priceMatch ? parseFloat(priceMatch[1]) : 0;
    }
    return 0;
  };

  // Helper function to parse price from matching cupcake string (e.g., "6 Cupcakes +€15" -> 15)
  const parseMatchingCupcakePrice = (cupcakeString: string): number => {
    if (cupcakeString.includes('+€')) {
      const priceMatch = cupcakeString.match(/\+€(\d+(?:\.\d+)?)/);
      return priceMatch ? parseFloat(priceMatch[1]) : 0;
    }
    return 0;
  };

  const uploadCakeImage = async (): Promise<string | undefined> => {
    if (!cakeImageFile) return undefined;
    
    try {
      setIsUploadingImage(true);
      const imageId = `cake-images/${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      await uploadFile(imageId, cakeImageFile);
      const imageUrl = await getFileURL(imageId);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading cake image:', error);
      throw new Error('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const uploadSampleImage = async (): Promise<string | undefined> => {
    if (!sampleImageFile) return undefined;
    
    try {
      setIsUploadingSampleImage(true);
      const imageId = `sample-images/${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      await uploadFile(imageId, sampleImageFile);
      const imageUrl = await getFileURL(imageId);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading sample image:', error);
      throw new Error('Failed to upload sample image. Please try again.');
    } finally {
      setIsUploadingSampleImage(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product || !selectedSize || selectedFlavors.length === 0) {
      return;
    }

    // Validate required color selections only when enabled for this product
    const requireBaseColor = product.enableBaseColor !== false;
    const requirePipingColor = product.enablePipingColor !== false;

    if (requireBaseColor && !selectedBaseColor) {
      showCartError('Please select a base color before adding to cart.');
      return;
    }

    if (requirePipingColor && selectedPipingColors.length === 0) {
      showCartError('Please select at least one piping color before adding to cart.');
      return;
    }

    // Validate required date selection
    if (deliveryOption === 'collection' && !selectedCollectionDate) {
      showCartError('Please select a collection date before adding to cart.');
      return;
    }

    if (deliveryOption === 'delivery' && !selectedDeliveryDate) {
      showCartError('Please select a delivery date before adding to cart.');
      return;
    }

    const selectedWindow = getFulfillmentWindowById(selectedTimeWindowId);
    if (!selectedWindow) {
      showCartError('Please select a time window before adding to cart.');
      return;
    }

    setIsAddingToCart(true);
    setAddToCartSuccess(false);

    try {
      // Upload cake image if provided
      let cakeImageUrl: string | undefined;
      if (hasImageOnCake && cakeImageFile) {
        cakeImageUrl = await uploadCakeImage();
      }

      // Upload sample image if provided
      let sampleImageUrl: string | undefined;
      if (hasSampleImage && sampleImageFile) {
        sampleImageUrl = await uploadSampleImage();
      }
      
      // Check if this is a quantity tier (for desserts)
      let quantityTierTotalPrice: number | undefined = undefined;
      if (selectedSize.quantityTiers && selectedSize.quantityTiers.length > 0) {
        const currentTier = selectedSize.quantityTiers.find(tier => tier.quantity === quantity);
        if (currentTier) {
          quantityTierTotalPrice = currentTier.price; // Total price for this quantity tier
        }
      }

      const cartItemData: CartItemCreateData = {
        productId: product.id,
        name: product.name,
        imageUrl: selectedImage || product.images[0]?.url || '',
        size: selectedSize.name,
        flavors: selectedFlavors,
        shape: selectedShape,
        layers: selectedSize.name, // Use the size name as layers since we removed the separate layers selector
        filling: selectedFilling,
        icing: selectedIcing,
        baseColor: selectedBaseColor,
        pipingColor: selectedPipingColors.join(', '),
        specialInstructions,
        addOns: selectedAddOns,
        matchingCupcakes: selectedMatchingCupcakes ? [selectedMatchingCupcakes] : [],
        hasImageOnCake: hasImageOnCake,
        cakeImageUrl: cakeImageUrl,
        hasSampleImage: hasSampleImage,
        sampleImageUrl: sampleImageUrl,
        deliveryOption,
        deliveryDate: deliveryOption === 'delivery' ? selectedDeliveryDate || undefined : 
                     deliveryOption === 'collection' ? selectedCollectionDate || undefined : undefined,
        timeWindowStart: selectedWindow.startTime,
        timeWindowEnd: selectedWindow.endTime,
        quantity,
        basePrice: selectedSize.price,
        quantityTierTotalPrice,
        flavorAdditionalPrice: selectedFlavors.reduce((total, flavorName) => {
          const flavor = product.flavors.find(f => f.name === flavorName);
          return total + (flavor?.price || 0);
        }, 0),
        fillingAdditionalPrice: selectedFilling ? parseFillingPrice(selectedFilling) : 0,
        icingAdditionalPrice: selectedIcing ? parseIcingPrice(selectedIcing) : 0,
        addOnsAdditionalPrice: selectedAddOns.reduce((total, addOn) => {
          return total + parseAddOnPrice(addOn);
        }, 0),
        matchingCupcakesAdditionalPrice: selectedMatchingCupcakes ? parseMatchingCupcakePrice(selectedMatchingCupcakes) : 0,
        imageOnCakeAdditionalPrice: hasImageOnCake ? (imageOnCakeSettings?.price || 15.00) : 0,
        baseColorAdditionalPrice: product.enableBaseColor !== false
          ? getBaseColorAdditionalPrice(selectedBaseColor, {
              collection: product.collection,
              productId: product.id,
              settings: colorPricingSettings
            })
          : 0
      };

      await operations.addItem(cartItemData);
      
      // Show success feedback
      setAddToCartSuccess(true);
      setTimeout(() => setAddToCartSuccess(false), 3000);
      
      // Show toast notification
      showAddToCartSuccess(product.name, quantity);
      
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      showCartError('Failed to add item to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setQuantity(value);
    }
  };

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
  };

  const handleBannerClick = (): void => {
    console.log('Banner arrow clicked');
  };

  const handleBackToCollection = () => {
    navigate(`/catalog/${collectionName}`);
  };

  const handleDeliveryDateSelect = (date: Date) => {
    setSelectedDeliveryDate(date);
    setIsCalendarExpanded(false); // Auto-collapse when date is selected
  };

  const handleCollectionDateSelect = (date: Date) => {
    setSelectedCollectionDate(date);
    setIsCalendarExpanded(false); // Auto-collapse when date is selected
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.custom-dropdown')) {
        setIsFlavorsDropdownOpen(false);
        setIsShapeDropdownOpen(false);
        setIsFillingDropdownOpen(false);
        setIsIcingDropdownOpen(false);
        setIsAddOnsDropdownOpen(false);
        setIsMatchingCupcakesDropdownOpen(false);
      }
    };

    if (isFlavorsDropdownOpen || isShapeDropdownOpen || isFillingDropdownOpen || isIcingDropdownOpen || isAddOnsDropdownOpen || isMatchingCupcakesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFlavorsDropdownOpen, isShapeDropdownOpen, isFillingDropdownOpen, isIcingDropdownOpen, isAddOnsDropdownOpen, isMatchingCupcakesDropdownOpen]);

  if (loading) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="loading-container">
          <LogoSpinner size="large" text="Loading product details..." />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="error-container">
          <div className="error-message">{error || 'Product not found'}</div>
          <button onClick={handleBackToCollection} className="back-button">
            Back to Collection
          </button>
        </div>
      </div>
    );
  }

  const colorPriceContext = {
    collection: product.collection,
    productId: product.id,
    settings: colorPricingSettings
  };
  const selectedBaseColorPrice = getBaseColorAdditionalPrice(
    selectedBaseColor,
    colorPriceContext
  );
  const whiteBaseColorPrice = getBaseColorAdditionalPrice('White', colorPriceContext);

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
      
      <div className="product-detail-page">
        <div className="container">
          <button onClick={handleBackToCollection} className="back-to-collection">
            ← Back to {collectionName?.replace(/-/g, ' ')}
          </button>
        <div className="product-detail-grid">
          <div className="product-images">
            <div className="main-image">
              <img src={selectedImage} alt={product.name} />
            </div>
            <div className="image-thumbnails">
              {product.images.map((image) => (
                <div 
                  key={image.id} 
                  className={`thumbnail ${selectedImage === image.url ? 'active' : ''}`}
                  onClick={() => setSelectedImage(image.url)}
                >
                  <img src={image.url} alt={image.alt} />
                </div>
              ))}
            </div>
          </div>

          <div className="product-detail-info">
            <div className="product-detail-heading">
              <h1 className="product-detail-title">{product.name}</h1>
              <span className="product-detail-price">€{totalPrice.toFixed(2)}</span>
            </div>
            <p className="product-detail-description">{product.description}</p>
            
            <div className="product-detail-option-section">
                <h3>Collection or Delivery</h3>
                <div className="delivery-options">
                  <div 
                    className={`delivery-option ${deliveryOption === 'collection' ? 'selected' : ''}`}
                    onClick={() => setDeliveryOption('collection')}
                  >
                    <div className="delivery-option-content">
                      <span className="delivery-option-title">Collection</span>
                      <span className="delivery-option-subtitle">Free - Pick up from our store</span>
                    </div>
                  </div>
                  <div 
                    className={`delivery-option ${deliveryOption === 'delivery' ? 'selected' : ''}`}
                    onClick={() => setDeliveryOption('delivery')}
                  >
                    <div className="delivery-option-content">
                      <span className="delivery-option-title">Delivery</span>
                      <span className="delivery-option-subtitle">
                        {minDeliveryPrice > 0 ? `Starts from €${minDeliveryPrice.toFixed(2)}` : 'Price varies by location'}
                      </span>
                    </div>
                  </div>
                </div>
                {deliveryOption === 'delivery' && minDeliveryPrice > 0 && (
                  <div className="delivery-note">
                    <p>Delivery price will be calculated based on your location during checkout.</p>
                  </div>
                )}
                <div className="fulfillment-window-row">
                  <div className="delivery-note">
                    {deliveryOption === 'delivery' ? (
                      <p>Delivery time — Tuesday-Sunday, 12pm - 5pm.</p>
                    ) : (
                      <p>
                        Collection time — Tuesday-Sunday, 12pm - 5pm.
                        <br />
                        Collection is at Airton Road, Dublin 24
                      </p>
                    )}
                  </div>
                  <div className="time-window-picker">
                    <label htmlFor="fulfillment-window">Time window *</label>
                    <select
                      id="fulfillment-window"
                      className="time-window-select"
                      value={selectedTimeWindowId}
                      onChange={(e) => setSelectedTimeWindowId(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        Select a window
                      </option>
                      {FULFILLMENT_WINDOWS.map((window) => (
                        <option key={window.id} value={window.id}>
                          {window.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Collection Date Selection */}
                {deliveryOption === 'collection' && (
                  <div className="delivery-date-section">
                    <div className="calendar-header" onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}>
                      <h4>Select Collection Date *</h4>
                      <span className="calendar-toggle">
                        {isCalendarExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                    {selectedCollectionDate && (
                      <div className="selected-date-summary">
                        <p>Selected: <strong>{selectedCollectionDate.toLocaleDateString('en-IE', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</strong></p>
                      </div>
                    )}
                    {isCalendarExpanded && (
                      <>
                        <Calendar
                          selectedDate={selectedCollectionDate || undefined}
                          onDateSelect={handleCollectionDateSelect}
                          availabilityDates={availabilityDates}
                          availabilitySettings={availabilitySettings}
                          className="delivery-calendar"
                        />
                        {selectedCollectionDate && (
                          <div className="selected-delivery-date">
                            <p>Selected collection date: <strong>{selectedCollectionDate.toLocaleDateString('en-IE', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</strong></p>
                          </div>
                        )}
                      </>
                    )}
                    {!selectedCollectionDate && (
                      <div className="date-selection-error">
                        <p>⚠️ Please select a collection date to continue</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Delivery Date Selection */}
                {deliveryOption === 'delivery' && (
                  <div className="delivery-date-section">
                    <div className="calendar-header" onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}>
                      <h4>Select Delivery Date *</h4>
                      <span className="calendar-toggle">
                        {isCalendarExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                    {selectedDeliveryDate && (
                      <div className="selected-date-summary">
                        <p>Selected: <strong>{selectedDeliveryDate.toLocaleDateString('en-IE', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</strong></p>
                      </div>
                    )}
                    {isCalendarExpanded && (
                      <>
                        <Calendar
                          selectedDate={selectedDeliveryDate || undefined}
                          onDateSelect={handleDeliveryDateSelect}
                          availabilityDates={availabilityDates}
                          availabilitySettings={availabilitySettings}
                          className="delivery-calendar"
                        />
                        {selectedDeliveryDate && (
                          <div className="selected-delivery-date">
                            <p>Selected delivery date: <strong>{selectedDeliveryDate.toLocaleDateString('en-IE', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</strong></p>
                          </div>
                        )}
                      </>
                    )}
                    {!selectedDeliveryDate && (
                      <div className="date-selection-error">
                        <p>⚠️ Please select a delivery date to continue</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            

              <div className="product-detail-options">
              <div className="product-detail-option-section">
                <h3>Size</h3>
                <div className="product-detail-size-options">
                  {product.sizes.map((size) => {
                    const isSelected = selectedSize?.name === size.name;
                    const isDessertsCollection = product.collection === 'Desserts';
                    const hasQuantityTiers = !!(size.quantityTiers && size.quantityTiers.length > 0);
                    const servesPerUnit = size.servingSize || 0;
                    const servesValue = isDessertsCollection
                      ? hasQuantityTiers
                        // Legacy tier-based desserts: tier quantity already represents total portions.
                        ? quantity
                        // Standard quantity selector: total servings = servings per unit * selected quantity.
                        : servesPerUnit * quantity
                      : size.servingSize;

                    // For Desserts, show the bundle price for the currently selected quantity (if tier exists)
                    let displayPrice = size.price;
                    if (isDessertsCollection && size.quantityTiers && size.quantityTiers.length > 0) {
                      const currentTier =
                        size.quantityTiers.find(tier => tier.quantity === quantity) ||
                        size.quantityTiers[0];
                      displayPrice = currentTier.price;
                    }

                    return (
                      <div 
                        key={size.name} 
                        className={`product-detail-size-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        <span className="product-detail-size-name">{size.name}</span>
                        <span className="product-detail-size-servings">Serves {servesValue}</span>
                        <span className="product-detail-size-price">€{displayPrice.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 2x2 Dropdown Grid */}
              <div className="dropdowns-grid">
                {/* Top Row: Flavors & Shape */}
                <div className="dropdown-row">
                  <div className="dropdown-section">
                    <h3>Flavors (Choose up to {product.maxFlavorsSelectable})</h3>
                    <div className="custom-dropdown">
                      <div 
                        className="custom-dropdown-trigger"
                        onClick={() => setIsFlavorsDropdownOpen(!isFlavorsDropdownOpen)}
                      >
                        <span className="dropdown-display">
                          {selectedFlavors.length === 0 
                            ? 'Select flavors' 
                            : selectedFlavors.length === 1 
                              ? selectedFlavors[0]
                              : `${selectedFlavors.length} flavors selected`
                          }
                        </span>
                        <span className="dropdown-arrow">
                          {isFlavorsDropdownOpen ? '▲' : '▼'}
                        </span>
                      </div>
                      {isFlavorsDropdownOpen && (
                        <div className="custom-dropdown-menu">
                          {product.flavors.map((flavor) => (
                            <div 
                              key={flavor.name}
                              className={`custom-dropdown-option ${selectedFlavors.includes(flavor.name) ? 'selected' : ''} ${!flavor.available ? 'disabled' : ''}`}
                              onClick={() => flavor.available && handleFlavorToggle(flavor.name)}
                            >
                              <span className="option-checkbox">
                                {selectedFlavors.includes(flavor.name) ? '✓' : ''}
                              </span>
                              <span className="option-text">
                                {flavor.name}
                                {flavor.price !== undefined && flavor.price !== null && 
                                  ` (${flavor.price === 0 ? '+€0' : `+€${flavor.price.toFixed(2)}`})`
                                }
                                {!flavor.available && ' - Unavailable'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedFlavors.length > 0 && (
                      <div className="selected-items">
                        Selected: {selectedFlavors.join(', ')}
                      </div>
                    )}
                  </div>

                  {product.availableShapes && product.availableShapes.length > 0 && (
                    <div className="dropdown-section">
                      <h3>Shape</h3>
                      <div className="custom-dropdown">
                        <div 
                          className="custom-dropdown-trigger"
                          onClick={() => setIsShapeDropdownOpen(!isShapeDropdownOpen)}
                        >
                          <span className="dropdown-display">
                            {selectedShape || 'Select a shape'}
                          </span>
                          <span className="dropdown-arrow">
                            {isShapeDropdownOpen ? '▲' : '▼'}
                          </span>
                        </div>
                        {isShapeDropdownOpen && (
                          <div className="custom-dropdown-menu">
                            {product.availableShapes.map((shape) => (
                              <div 
                                key={shape}
                                className={`custom-dropdown-option ${selectedShape === shape ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedShape(shape);
                                  setIsShapeDropdownOpen(false);
                                }}
                              >
                                <span className="option-checkbox">
                                  {selectedShape === shape ? '✓' : ''}
                                </span>
                                <span className="option-text">{shape}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Row: Filling & Cover Icing */}
                <div className="dropdown-row">
                  {product.fillings && Array.isArray(product.fillings) && product.fillings.length > 0 && (
                    <div className="dropdown-section">
                      <h3>Filling</h3>
                      <div className="custom-dropdown">
                        <div 
                          className="custom-dropdown-trigger"
                          onClick={() => setIsFillingDropdownOpen(!isFillingDropdownOpen)}
                        >
                          <span className="dropdown-display">
                            {selectedFilling || 'Select a filling'}
                          </span>
                          <span className="dropdown-arrow">
                            {isFillingDropdownOpen ? '▲' : '▼'}
                          </span>
                        </div>
                        {isFillingDropdownOpen && (
                          <div className="custom-dropdown-menu">
                            {product.fillings.map((filling) => {
                              // Check if filling contains price information
                              const hasPriceInfo = filling.includes('+€');
                              const fillingName = hasPriceInfo ? filling.split('+€')[0].trim() : filling;
                              const priceInfo = hasPriceInfo ? '+€' + filling.split('+€')[1].trim() : '';
                              
                              return (
                                <div 
                                  key={filling}
                                  className={`custom-dropdown-option ${selectedFilling === filling ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedFilling(filling);
                                    setIsFillingDropdownOpen(false);
                                  }}
                                >
                                  <span className="option-checkbox">
                                    {selectedFilling === filling ? '✓' : ''}
                                  </span>
                                  <span className="option-text">
                                    {fillingName}
                                    {hasPriceInfo && <span className="dropdown-price">{priceInfo}</span>}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {product.coverIcingOptions && Array.isArray(product.coverIcingOptions) && product.coverIcingOptions.length > 0 && (
                    <div className="dropdown-section">
                      <h3>Cover Icing</h3>
                      <div className="custom-dropdown">
                        <div 
                          className="custom-dropdown-trigger"
                          onClick={() => setIsIcingDropdownOpen(!isIcingDropdownOpen)}
                        >
                          <span className="dropdown-display">
                            {selectedIcing || 'Select cover icing'}
                          </span>
                          <span className="dropdown-arrow">
                            {isIcingDropdownOpen ? '▲' : '▼'}
                          </span>
                        </div>
                        {isIcingDropdownOpen && (
                          <div className="custom-dropdown-menu">
                            {product.coverIcingOptions.map((icing) => {
                              // Check if icing contains price information
                              const hasPriceInfo = icing.includes('+€');
                              const icingName = hasPriceInfo ? icing.split('+€')[0].trim() : icing;
                              const priceInfo = hasPriceInfo ? '+€' + icing.split('+€')[1].trim() : '';
                              
                              return (
                                <div 
                                  key={icing}
                                  className={`custom-dropdown-option ${selectedIcing === icing ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedIcing(icing);
                                    setIsIcingDropdownOpen(false);
                                  }}
                                >
                                  <span className="option-checkbox">
                                    {selectedIcing === icing ? '✓' : ''}
                                  </span>
                                  <span className="option-text">
                                    {icingName}
                                    {hasPriceInfo && <span className="dropdown-price">{priceInfo}</span>}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add-ons and Matching Cupcakes Row */}
              {(product.addOns && product.addOns.length > 0) || (product.matchingCupcakes && product.matchingCupcakes.length > 0) ? (
                <div className="dropdowns-grid">
                  <div className="dropdown-row">
                    {product.addOns && product.addOns.length > 0 && (
                      <div className="dropdown-section">
                        <h3>Add-ons</h3>
                        <div className="custom-dropdown">
                          <div 
                            className="custom-dropdown-trigger"
                            onClick={() => setIsAddOnsDropdownOpen(!isAddOnsDropdownOpen)}
                          >
                            <span className="dropdown-display">
                              {selectedAddOns.length === 0 
                                ? 'Select add-ons' 
                                : selectedAddOns.length === 1 
                                  ? selectedAddOns[0].split('+€')[0].trim()
                                  : `${selectedAddOns.length} add-ons selected`
                              }
                            </span>
                            <span className="dropdown-arrow">
                              {isAddOnsDropdownOpen ? '▲' : '▼'}
                            </span>
                          </div>
                          {isAddOnsDropdownOpen && (
                            <div className="custom-dropdown-menu">
                              {product.addOns.map((addOn) => {
                                const hasPriceInfo = addOn.includes('+€');
                                const addOnName = hasPriceInfo ? addOn.split('+€')[0].trim() : addOn;
                                const priceInfo = hasPriceInfo ? '+€' + addOn.split('+€')[1].trim() : '';
                                
                                return (
                                  <div 
                                    key={addOn}
                                    className={`custom-dropdown-option ${selectedAddOns.includes(addOn) ? 'selected' : ''}`}
                                    onClick={() => handleAddOnToggle(addOn)}
                                  >
                                    <span className="option-checkbox">
                                      {selectedAddOns.includes(addOn) ? '✓' : ''}
                                    </span>
                                    <span className="option-text">
                                      {addOnName}
                                      {hasPriceInfo && <span className="dropdown-price">{priceInfo}</span>}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {selectedAddOns.length > 0 && (
                          <div className="selected-items">
                            Selected: {selectedAddOns.map(addOn => addOn.split('+€')[0].trim()).join(', ')}
                          </div>
                        )}
                      </div>
                    )}

                    {product.matchingCupcakes && product.matchingCupcakes.length > 0 && (
                      <div className="dropdown-section">
                        <h3>Matching Cupcakes</h3>
                        <div className="custom-dropdown">
                          <div 
                            className="custom-dropdown-trigger"
                            onClick={() => setIsMatchingCupcakesDropdownOpen(!isMatchingCupcakesDropdownOpen)}
                          >
                            <span className="dropdown-display">
                              {selectedMatchingCupcakes 
                                ? selectedMatchingCupcakes.split('+€')[0].trim()
                                : 'Select matching cupcakes'
                              }
                            </span>
                            <span className="dropdown-arrow">
                              {isMatchingCupcakesDropdownOpen ? '▲' : '▼'}
                            </span>
                          </div>
                          {isMatchingCupcakesDropdownOpen && (
                            <div className="custom-dropdown-menu">
                              {product.matchingCupcakes.map((cupcake) => {
                                const hasPriceInfo = cupcake.includes('+€');
                                const cupcakeName = hasPriceInfo ? cupcake.split('+€')[0].trim() : cupcake;
                                const priceInfo = hasPriceInfo ? '+€' + cupcake.split('+€')[1].trim() : '';
                                
                                return (
                                  <div 
                                    key={cupcake}
                                    className={`custom-dropdown-option ${selectedMatchingCupcakes === cupcake ? 'selected' : ''}`}
                                    onClick={() => handleMatchingCupcakeSelect(cupcake)}
                                  >
                                    <span className="option-checkbox">
                                      {selectedMatchingCupcakes === cupcake ? '✓' : ''}
                                    </span>
                                    <span className="option-text">
                                      {cupcakeName}
                                      {hasPriceInfo && <span className="dropdown-price">{priceInfo}</span>}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {selectedMatchingCupcakes && (
                          <div className="selected-items">
                            Selected: {selectedMatchingCupcakes.split('+€')[0].trim()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Base and piping colors - shown/required only when enabled on the product */}
              {product.enableBaseColor !== false && (
                <div className="product-detail-option-section">
                  <h3>
                    Base Color *
                    {selectedBaseColor
                      ? ` | ${selectedBaseColor}${
                          selectedBaseColorPrice > 0
                            ? ` +€${selectedBaseColorPrice.toFixed(2)}`
                            : ''
                        }`
                      : ''}
                  </h3>
                  <div className="color-selector">
                    {availableColors.map((color) => {
                      const colorPrice = getBaseColorAdditionalPrice(color.name, colorPriceContext);
                      return (
                        <div
                          key={color.name}
                          className={`color-option ${selectedBaseColor === color.name ? 'selected' : ''}`}
                          onClick={() => setSelectedBaseColor(color.name)}
                          title={
                            colorPrice > 0
                              ? `${color.name} +€${colorPrice.toFixed(2)}`
                              : color.name
                          }
                        >
                          <div 
                            className="color-swatch"
                            style={{ 
                              backgroundColor: color.value,
                              borderColor: selectedBaseColor === color.name ? '#FF0000' : color.border
                            }}
                          >
                            {selectedBaseColor === color.name && (
                              <span className="color-checkmark">✓</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="color-price-note">
                    White base colour adds €{whiteBaseColorPrice.toFixed(2)}. Piping colour is not charged.
                  </p>
                  {!selectedBaseColor && (
                    <div className="color-selection-error">
                      <p>⚠️ Please select a base color to continue</p>
                    </div>
                  )}
                </div>
              )}

              {product.enablePipingColor !== false && (
                <div className="product-detail-option-section">
                  <h3>Piping Color * (Choose up to 2){selectedPipingColors.length > 0 && ` | ${selectedPipingColors.join(', ')}`}</h3>
                  <div className="color-selector">
                    {availableColors.map((color) => (
                      <div
                        key={color.name}
                        className={`color-option ${selectedPipingColors.includes(color.name) ? 'selected' : ''}`}
                        onClick={() => handlePipingColorToggle(color.name)}
                        title={color.name}
                      >
                        <div 
                          className="color-swatch"
                          style={{ 
                            backgroundColor: color.value,
                            borderColor: selectedPipingColors.includes(color.name) ? '#FF0000' : color.border
                          }}
                        >
                          {selectedPipingColors.includes(color.name) && (
                            <span className="color-checkmark">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedPipingColors.length > 0 && (
                    <div className="selected-colors-info">
                      <p>Selected: {selectedPipingColors.join(', ')}</p>
                    </div>
                  )}
                  {selectedPipingColors.length === 0 && (
                    <div className="color-selection-error">
                      <p>⚠️ Please select at least one piping color to continue</p>
                    </div>
                  )}
                </div>
              )}

              {product.enableMessageOnCake !== false && (
                <div className="product-detail-option-section">
                  <h3>Message on cake</h3>
                  <textarea
                    className="special-instructions"
                    placeholder="Add your message for the cake..."
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              {product.enableImageOnCake !== false && (
                <div className="product-detail-option-section">
                  <h3>Image on cake</h3>
                  <div className="image-upload-section">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={hasImageOnCake}
                        onChange={(e) => setHasImageOnCake(e.target.checked)}
                      />
                      <span className="checkbox-text">I want to add an edible image to my cake +€{imageOnCakeSettings?.price?.toFixed(2) || '15.00'}</span>
                    </label>
                    
                    {hasImageOnCake && (
                      <div className="image-upload-container">
                        <div className="image-upload-area">
                          {cakeImagePreview ? (
                            <div className="image-preview">
                              <img src={cakeImagePreview} alt="Cake image preview" />
                              <button
                                type="button"
                                className="remove-image-btn"
                                onClick={handleRemoveImage}
                              >
                                Remove Image
                              </button>
                            </div>
                          ) : (
                            <div className="upload-prompt">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="file-input"
                                id="cake-image-upload"
                              />
                              <label htmlFor="cake-image-upload" className="upload-label">
                                <span className="upload-icon">📷</span>
                                <span className="upload-text">Click to upload image</span>
                                <span className="upload-hint">Max 5MB, JPG/PNG recommended</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {product.enableSampleImage !== false && (
                <div className="product-detail-option-section">
                  <h3>Sample cake image (Optional)</h3>
                  <div className="sample-image-upload-section">
                    <h4>Upload a sample cake image</h4>
                    <p>If you have a reference image or example of what you'd like your cake to look like, you can upload it here. This will help our team understand your vision better.</p>
                    
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={hasSampleImage}
                        onChange={(e) => setHasSampleImage(e.target.checked)}
                      />
                      <span className="checkbox-text">I want to upload a sample cake image</span>
                    </label>
                    
                    {hasSampleImage && (
                      <div className="image-upload-container">
                        <aside className="sample-image-disclaimer" role="note">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                              d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <div className="sample-image-disclaimer__content">
                            <p className="sample-image-disclaimer__title">Please note</p>
                            <p className="sample-image-disclaimer__text">
                              The finished cake may differ from your sample photo. Each cake is handmade, so small variations are natural. Sample images are for inspiration only — we cannot recreate a design exactly.
                            </p>
                          </div>
                        </aside>
                        <div className="sample-image-upload-area">
                          {sampleImagePreview ? (
                            <div className="sample-image-preview">
                              <img src={sampleImagePreview} alt="Sample cake image preview" />
                              <button
                                type="button"
                                className="sample-remove-image-btn"
                                onClick={handleRemoveSampleImage}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div className="sample-upload-prompt">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleSampleImageUpload}
                                className="file-input"
                                id="sample-image-upload"
                              />
                              <label htmlFor="sample-image-upload" className="sample-upload-label">
                                <span className="sample-upload-icon">🖼️</span>
                                <span className="sample-upload-text">Click to upload sample image</span>
                                <span className="sample-upload-hint">Max 5MB, JPG/PNG recommended</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="quantity-section">
                <h3>Quantity</h3>
                {selectedSize?.quantityTiers && selectedSize.quantityTiers.length > 0 ? (
                  <div className="quantity-tier-selector">
                    <div className="quantity-tier-options">
                      {selectedSize.quantityTiers.map(tier => (
                        <button
                          key={tier.quantity}
                          type="button"
                          className={`quantity-tier-option ${quantity === tier.quantity ? 'selected' : ''}`}
                          onClick={() => setQuantity(tier.quantity)}
                        >
                          {tier.quantity}
                        </button>
                      ))}
                    </div>
                    <p className="quantity-help-text">
                      Select one of the available quantities for this product.
                    </p>
                  </div>
                ) : (
                  <div className="quantity-selector">
                    <button 
                      className="quantity-btn" 
                      onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                    >
                      -
                    </button>
                    <input 
                      type="number" 
                      min="1" 
                      value={quantity} 
                      onChange={handleQuantityChange} 
                      className="quantity-input"
                    />
                    <button 
                      className="quantity-btn" 
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>

              <div className="add-to-cart-section">
                <Button 
                  variant="primary" 
                  size="large" 
                  onClick={handleAddToCart}
                  className={`add-to-cart-btn ${addToCartSuccess ? 'success' : ''}`}
                  disabled={
                    isAddingToCart ||
                    isUploadingImage ||
                    isUploadingSampleImage ||
                    !product ||
                    !selectedSize ||
                    selectedFlavors.length === 0 ||
                    // Only enforce color selections when enabled for this product
                    ((product.enableBaseColor !== false) && !selectedBaseColor) ||
                    ((product.enablePipingColor !== false) && selectedPipingColors.length === 0) ||
                    (deliveryOption === 'collection' && !selectedCollectionDate) ||
                    (deliveryOption === 'delivery' && !selectedDeliveryDate) ||
                    !selectedTimeWindowId
                  }
                >
                  {isAddingToCart || isUploadingImage || isUploadingSampleImage ? (
                    <>
                      <LogoSpinner size="small" />
                      {isUploadingImage ? 'Uploading Image...' : isUploadingSampleImage ? 'Uploading Sample...' : 'Adding to Cart...'}
                    </>
                  ) : addToCartSuccess ? (
                    <>
                      <span className="success-icon">✓</span>
                      Added to Cart!
                    </>
                  ) : (
                    `Add to Cart - €${totalPrice.toFixed(2)}`
                  )}
                </Button>
                
                {addToCartSuccess && (
                  <div className="add-to-cart-success-message">
                    Item added to cart successfully! 
                    <button 
                      onClick={() => navigate('/cart')}
                      className="view-cart-link"
                    >
                      View Cart
                    </button>
                  </div>
                )}
              </div>

              {/* Cake Care and Disclaimer Accordion */}
              <div className="cake-care-section">
                <Accordion 
                  items={[
                    {
                      id: 'cake-care',
                      title: 'Cake Care Instructions',
                      content: (
                        <div className="cake-care-content">
                          <ul>
                            <li>Carry the cake from the bottom of the box at all times</li>
                            <li>Make sure the cake is placed on a flat surface when transporting</li>
                            <li>Place the cake in the fridge, DO NOT place in direct sunlight</li>
                            <li>Take the cake out of the fridge 1-2 hours before serving</li>
                            <li>Place the cake on a stable cake stand for display</li>
                            <li>Place leftovers in an airtight container in the fridge</li>
                            <li>Remove all inedible decorations and props</li>
                            <li>Consume within 5-7 days (must be refrigerated)</li>
                          </ul>
                        </div>
                      )
                    },
                    {
                      id: 'disclaimer',
                      title: 'Allergen Disclaimer',
                      content: (
                        <div className="disclaimer-content">
                          <p>
                            Please note that our cakes may contain common allergens such as nuts, dairy, eggs, and gluten. 
                            If you have any allergies or dietary concerns, please let us know before ordering.
                          </p>
                        </div>
                      )
                    }
                  ]}
                  className="cake-care-accordion"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default ProductDetailPage;
